import { Button, Form, Input, message, Select } from "antd";
import { FC, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutSite } from "../components/layout";
import { Loader } from "../components/loader";
import
{
  import_file_qa,
  preview_import_file_qa,
} from "../services/api.service";
import "./importqa.scss";
import { APIAnswer, APIAnswerData, MLCompetitor, MLRace } from "../types";
import { ArrowLeftOutlined, DownOutlined, FileAddOutlined, FileExcelOutlined, InfoCircleOutlined, LoadingOutlined, UpOutlined } from "@ant-design/icons";

/* -------------------------------------------------------------------------------- *\
|                               ImportQAPage
\* -------------------------------------------------------------------------------- */
export const ImportQAPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
  const [loading, setLoading] = useState(true);
  const [previewraces, setPreviewRaces] = useState<MLRace[]>([]);
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [openRaces, setOpenRaces] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  /* -------------------------------------------------------------------------------- *\
  |                               effects
  \* -------------------------------------------------------------------------------- */
  useEffect(() =>
  {
    setLoading(true);
    setLoading(false);
    // eslint-disable-next-line
  }, []);

  /* -------------------------------------------------------------------------------- *\
  |                               toggleRace
  \* -------------------------------------------------------------------------------- */
  function toggleRace(index: number)
  {
    const newOpenRaces = new Set(openRaces);
    if (newOpenRaces.has(index))
    {
      newOpenRaces.delete(index);
    } else
    {
      newOpenRaces.add(index);
    }
    setOpenRaces(newOpenRaces);
  }

  function toggleAllRaces()
  {
    if (openRaces.size > 0)
    {
      setOpenRaces(new Set());
    } else
    {
      setOpenRaces(new Set(previewraces.map((_, i) => i)));
    }
  }

  /* -------------------------------------------------------------------------------- *\
  |                               processFilePreview
  \* -------------------------------------------------------------------------------- */
  function processFilePreview(fileToProcess: File)
  {
    const sic = form.getFieldValue("sic");
    var reader = new FileReader();

    reader.addEventListener("load", (e) =>
    {
      if (e.loaded === e.total)
      {
        try
        {
          const result = e.target?.result as string;
          const base64 = result.split(",")[1];

          setErrorMessage(null);
          setPreviewRaces([]);
          setIsUploading(true);

          preview_import_file_qa(base64, sic)
            .then((data: APIAnswerData) =>
            {
              if (data.status)
              {
                setPreview(base64);
                setPreviewRaces(data.data);
                setOpenRaces(new Set());
                setErrorMessage(null);
              } else
              {
                setPreview("");
                setErrorMessage(data.message);
                setPreviewRaces([]);
              }
            })
            .catch((error) =>
            {
              setErrorMessage(error.message);
              setPreview("");
              setPreviewRaces([]);
              message.error(error.message);
            })
            .finally(() =>
            {
              setIsUploading(false);
            });
        } catch (error)
        {
          setPreview("");
          setErrorMessage("Error reading file : " + error);
          setIsUploading(false);
        }
      }
    });

    reader.readAsDataURL(fileToProcess);
  }

  /* -------------------------------------------------------------------------------- *\
  |                               handleTrackChange
  \* -------------------------------------------------------------------------------- */
  function handleTrackChange()
  {
    // Re-trigger preview with new track if file is already loaded
    if (file)
    {
      processFilePreview(file);
    }
  }

  /* -------------------------------------------------------------------------------- *\
  |                               handleUpload
  \* -------------------------------------------------------------------------------- */
  function handleUpload(event)
  {
    const e: HTMLInputElement = event.target;

    if (e.files && e.files[0])
    {
      const f: File = e.files[0];
      const sic = form.getFieldValue("sic");

      if (!sic)
      {
        message.warning("Please select a track first");
        setFileInputKey(prev => prev + 1);
        return;
      }

      setFile(f);
      processFilePreview(f);
    }

    form.setFieldsValue({ file: null });
    // Force React to recreate the input by changing its key
    setFileInputKey(prev => prev + 1);
  }
  /* -------------------------------------------------------------------------------- *\
|                               onFinishUpload
\* -------------------------------------------------------------------------------- */
  function onFinishUpload()
  {
    var reader = new FileReader();
    const sic = form.getFieldValue("sic");

    reader.addEventListener("load", (e) =>
    {
      if (e.loaded === e.total)
      {
        const result = e.target?.result as string;
        const base64 = result.split(",")[1];

        import_file_qa(base64, sic)
          .then((answer: APIAnswer) =>
          {
            if (answer.status)
            {
              message.success("Import réussi");
              navigate("/");
            } else
            {
              message.error(answer.message || "Erreur lors de l'import");
            }
          })
          .catch((error) =>
          {
            message.error(error.message || "Erreur lors de l'import");
          });
      }
    });
    reader.readAsDataURL(file as Blob);
  }

  return (
    <LayoutSite>
      <div className="importqa">
        {loading ? <Loader /> : <></>}
        <div className="importqa-header">
          <button className="importqa-header-action" onClick={() => navigate("/calendar")}>
            <ArrowLeftOutlined />
            Calendar
          </button>
        </div>
        <div className="importqa-body">
          <h1 className="importqa-title">
            Import race from EXCEL file {file?.name}
          </h1>
          <Form
            form={form}
            name="importqa-form"
            className="importqa-form"
            onFinish={() => onFinishUpload()}
            layout="vertical"
          >
            <Form.Item name="sic" label="Track" rules={[{ required: true }]}>
              <Select
                placeholder="Select a track"
                options={[
                  { value: "RAY", label: "AL RAYYAN" },
                  { value: "UQD", label: "AL UQDA" },
                ]}
                onChange={handleTrackChange}
              />
            </Form.Item>
            <Form.Item name="file" label="File">
              <div className="importqa-form-file-wrapper">
                <div className="importqa-form-file-container">
                  <FileAddOutlined style={{ fontSize: "24px" }} />
                  <h4 className="importqa-form-file-title">Add your excel file for see the preview or import</h4>
                </div>
                <Input className="importqa-form-file" type="file" hidden={true} onChange={handleUpload} key={fileInputKey} />
              </div>
            </Form.Item>
            {
              file &&
              <p className="importqa-form-file-name">
                <FileExcelOutlined style={{ fontSize: "16px" }} />
                {file?.name}
              </p>
            }
            <Form.Item className="importqa-form-actions">
              <Button className="importqa-form-import" type="primary" htmlType="submit" disabled={isUploading || !!errorMessage || !file}>
                Import
              </Button>
            </Form.Item>
          </Form>
          {errorMessage && (
            <div className="importqa-error">
              <div className="importqa-error-content">
                <InfoCircleOutlined style={{ color: 'hsl(0, 84%, 60%)' }} />
                <p className="importqa-error-text">{errorMessage}</p>
              </div>
            </div>
          )}
          {
            isUploading && (
              <>
                <LoadingOutlined style={{ fontSize: "24px" }} />
              </>
            )
          }
          {preview && !errorMessage && !isUploading && (
            <>
              <Button className="importqa-preview-close" type="primary" onClick={toggleAllRaces}>{openRaces.size > 0 ? "Closed All" : "Open All"}</Button>
              <div className="importqa-preview">
                <div className="importqa-preview-races">
                  {previewraces?.map((race: MLRace, index: number) =>
                  {
                    const date = new Date(race.date);

                    return (
                      <div
                        className={"importqa-preview-race"}
                        key={index}
                        onClick={() => toggleRace(index)}
                      >
                        <div className="importqa-preview-race-container">
                          <div className="importqa-preview-race-line">
                            <div>C{race.number}</div>
                            <div>{new Date(race.meetingdate).toLocaleDateString()}</div>
                            <div>
                              {!isNaN(date.getTime())
                                ? date.toLocaleTimeString().substring(0, 5)
                                : ""}
                            </div>
                            <div> - {race.distance}m</div>
                            <div> - <span className={race.trackcode === "XXX" ? "importqa-trackcode-invalid" : undefined}>{race.trackcode}</span></div>
                            <div>{" - count=" + race.competitors.length}</div>
                            <div> {race.name}</div>
                          </div>
                          <div
                            style={{ cursor: 'pointer' }}
                          >
                            {openRaces.has(index) ? <UpOutlined style={{ fontSize: "20px" }} /> : <DownOutlined style={{ fontSize: "20px" }} />}
                          </div>
                        </div>
                        {openRaces.has(index) && race.competitors.map(
                          (horse: MLCompetitor, index: number) =>
                          {
                            return (
                              <div
                                key={index}
                                className="importqa-preview-race-horse"
                              >
                                <div>{horse.number}</div>
                                <div key={index}>
                                  {horse.urlsilk && (
                                    <img
                                      src={horse.urlsilk}
                                      alt={horse.name}
                                      style={{ width: 25, height: 25 }}
                                    />
                                  )}
                                </div>
                                <p>{horse.name}</p>
                              </div>
                            );
                          }
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </LayoutSite>
  );
};
